import React, { useState, useEffect, ReactElement } from 'react';
import axios from 'axios';
import Dropzone from 'react-dropzone';
import { useSelector } from 'react-redux';
import { Button, Modal, Icon, DefaultModalDialog } from '../../../../ps-aspect-design';
import './EditPricingVersionDetailsUploadModal.scss';
import client from '@payscale/dyson-api/lib/client';
import { UploadSet } from '@payscale/dyson-api/lib/types';
import { UploadError } from '../types/versions';
type UploadMatchStatus = 'initial' | 'loading' | 'success' | 'partial' | 'failure';

const EditPricingVersionDetailsUploadModal = (props: {
  pricingVersionId: string;
  isOpen: boolean;
  onClose: () => void;
  launchDarklyFeatureFlags: { [key: string]: boolean };
  onSuccess?: () => void;
  getUploadErrors: any;
  downloadUploadErrorsToExcel: any;
}): ReactElement => {
  const { pricingVersionId, isOpen, onClose, onSuccess, getUploadErrors, downloadUploadErrorsToExcel } = props;
  const [uploadMatchStatus, setUploadMatchStatus] = useState<UploadMatchStatus>('initial');
  const [uploadMatchFileName, setUploadMatchFileName] = useState<string>();
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([]);
  const {
    organization: { code, masterAccountId },
    csrfPair,
    hostname: productBaseUrl,
    user: { email }
  } = useSelector((state: any) => state.session);

  useEffect(() => {
    if (isOpen) {
      setUploadMatchStatus('initial');
    }
  }, [isOpen]);

  useEffect(() => {
    if (uploadMatchStatus === "loading") {
      setUploadErrors([]);
    }
  }, [uploadMatchStatus]);

  const getErrorCount = (rows: UploadError[]) => {
    const columnValues = ['global_job_pricing_version_id', 'match_priority', 'created_by', 'created', 'org_job_id'];
    return rows.filter(error => !columnValues.includes(error['column_name'])).length;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="edit-pricing-version-details-upload-modal">
      <DefaultModalDialog
        title={`Upload Match Priorities ${
          props.launchDarklyFeatureFlags['global-job-matching-dyson-imports'] ? '(Dyson)' : ''
        } `}
        customFooter={undefined}
      >
        <p>
          Please use this template to ensure the file contains all required elements.
          <br />
          <Button
            href={`/${code}/merit-${code}/merit-admin/gen-upload-file?file_name=${encodeURIComponent(
              'job_matching_matches_sample.csv'
            )}&source_table=${encodeURIComponent('mp_upload_org_job_survey_job_map_columns')}`}
            variant="link"
          >
            Download Template
          </Button>
        </p>
        {uploadMatchStatus === 'failure' ? (
          <>
            <p>
              <Icon className="edit-pricing-version-details-upload-modal__warning-icon" name="Warning" />
              {`Sorry we are not able to upload${
                uploadMatchFileName ? ` "${uploadMatchFileName}"` : ''
              }. `}
            </p>
            <p>{`There are ${getErrorCount(uploadErrors)} errors.  Download error file `}
              <Button href='#' onClick={() => downloadUploadErrorsToExcel(uploadErrors, csrfPair)} variant="link">
                here
              </Button>
            </p>
            <p>Please fix the errors and try again or select a different file.</p>
          </>
        ) : null}
        {uploadMatchStatus === 'initial' || uploadMatchStatus === 'failure' ? (
          <Dropzone
            onDrop={(): void => setUploadMatchStatus('loading')}
            onDropAccepted={async (files): Promise<void> => {
              setUploadMatchFileName(files[0].name);
              try {
                if (props.launchDarklyFeatureFlags['global-job-matching-dyson-imports']) {
                  console.log('uploading with @payscale/upload'); // eslint-disable-line no-console
                  // upload the file
                  const uploadSetResponse = await client.uploadSet.post(
                    masterAccountId,
                    files,
                    `https://${productBaseUrl}`,
                    'other',
                    true
                  );
                  if (!uploadSetResponse?.data) {
                    throw new Error('Failed to upload');
                  }
                  const uploadSet = uploadSetResponse.data as UploadSet;

                  // fetch the match priority fields and find the version id field
                  const fieldsResponse = await client.fields.get(masterAccountId, ['MatchPriority']);
                  const versionField = fieldsResponse.data.find(
                    field => field.canonicalName === 'globalJobPricingVersionId'
                  );
                  if (!versionField) {
                    throw new Error('Error fetching match priority fields');
                  }

                  // set category
                  const categoryResponse = await client.dataFrame.put(
                    masterAccountId,
                    uploadSet.id,
                    uploadSet.uploadFiles[0].id,
                    uploadSet.uploadFiles[0].dataFrames[0].id,
                    {
                      id: uploadSet.uploadFiles[0].dataFrames[0].id,
                      category: 'match_priority'
                    }
                  );
                  if (!categoryResponse.data) {
                    throw new Error('Error setting category');
                  }

                  // set upload mode
                  const uploadModeResponse = await client.dataFrame.put(
                    masterAccountId,
                    uploadSet.id,
                    uploadSet.uploadFiles[0].id,
                    uploadSet.uploadFiles[0].dataFrames[0].id,
                    {
                      id: uploadSet.uploadFiles[0].dataFrames[0].id,
                      uploadMode: 'update_and_add'
                    }
                  );
                  if (!uploadModeResponse.data) {
                    throw new Error('Error setting upload mode');
                  }

                  // check if the version id field is present in the dataframe
                  const dataFrameColumnsResponse = await client.columnMapping.dataFrameColumns.get({
                    masterAccountId,
                    uploadSetId: uploadSet.id,
                    uploadFileId: uploadSet.uploadFiles[0].id,
                    dataFrameId: uploadSet.uploadFiles[0].dataFrames[0].id
                  });
                  if (!dataFrameColumnsResponse.data) {
                    throw new Error('Error fetching data frame columns');
                  }
                  const existingVersionIdField = dataFrameColumnsResponse.data.find(
                    column => column.field?.id === versionField.id
                  );
                  if (existingVersionIdField) {
                    throw new Error('Global Job Pricing Version Id should not be present in the upload');
                  }

                  // add a new column with the version id
                  const addColumnResponse = await client.columnMapping.dataFrameColumns.add(
                    {
                      masterAccountId,
                      uploadSetId: uploadSet.id,
                      uploadFileId: uploadSet.uploadFiles[0].id,
                      dataFrameId: uploadSet.uploadFiles[0].dataFrames[0].id
                    },
                    versionField.id,
                    pricingVersionId,
                    false
                  );
                  if (!addColumnResponse.data) {
                    throw new Error('Error attaching pricing version id to the upload');
                  }

                  // auto-submit
                  const submitResponse = await client.uploadSet.autoSubmit(masterAccountId, uploadSet.id, {
                    userEmail: email
                  });
                  if (submitResponse.status !== 202) {
                    throw new Error('Error submitting upload');
                  }
                } else {
                  // upload via marketpay acs
                  const formData = new FormData();
                  formData.append('import_mode', 'erase_reload');
                  formData.append('table_name', 'org_job_survey_job_map');
                  formData.append('pricing_version_id', pricingVersionId);
                  formData.append('csrf_token', csrfPair.csrf_token);
                  formData.append('csrf_timestamp', csrfPair.csrf_timestamp);
                  formData.append('the_file', files[0]);
                  const response = await axios.post(
                    `/${code}/merit-${code}/merit-admin/import/file-upload-new-match-priority`,
                    formData,
                    {
                      headers: {
                        'Content-Type': 'text/csv'
                      }
                    }
                  );
                  if (response.data?.success) {
                    await axios.get(`/${code}/merit-${code}/merit-admin/import/${response.data?.msg}`);
                  } else {
                    throw new Error('Error importing match priority upload');
                  }
                }
                setUploadMatchStatus('success');
                onSuccess && onSuccess();
              } catch (error) {
                const uploadErrors = (await getUploadErrors()).data?.response?.rows;

                if (uploadErrors?.length > 0) {
                  setUploadErrors(uploadErrors);
                }
                setUploadMatchStatus('failure');
              }
            }}
            onDropRejected={(): void => setUploadMatchStatus('failure')}
            multiple={false}
            accept={'.csv'}
            maxSize={25 * 1048576}
          >
            {
              ({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} className='edit-pricing-version-details-upload-modal__dropzone'>
                  <Icon className="edit-pricing-version-details-upload-modal__upload-icon" name="Upload" />
                  <p>
                    <label className='edit-pricing-version-details-upload-modal__select-file'>Select File</label>
                    <input {...getInputProps()} /> or drag and drop file here
                  </p>
                </div>
              )
            }
          </Dropzone>
        ) : null}
        {uploadMatchStatus === 'loading' ? (
          <div className="edit-pricing-version-details-upload-modal__content">
            <Icon className="edit-pricing-version-details-upload-modal__loading-icon" name="Cw" spin={true} />
            <p className="edit-pricing-version-details-upload-modal__loading-text">{`Uploading and processing data${
              uploadMatchFileName ? ` in "${uploadMatchFileName}"` : ''
            }...`}</p>
          </div>
        ) : null}
        {uploadMatchStatus === 'success' ? (
          <div className="edit-pricing-version-details-upload-modal__content">
            <Icon className="edit-pricing-version-details-upload-modal__success-icon" wrapper="circle" name="Check" />
            <p>The data in this file was processed successfully.</p>
            <Button onClick={onClose} variant="link">
              Return to Pricing Version
            </Button>
          </div>
        ) : null}
      </DefaultModalDialog>
    </Modal>
  );
};

export default EditPricingVersionDetailsUploadModal;
